import { useState, useEffect } from 'react';
import { ArrowLeft, Download, X, QrCode, Barcode } from 'lucide-react';
// import { Button } from './Button';
import { useTicketStore } from '../store/ticketStore';
import { useNavigation } from '../hooks/useNavigation';

export const Ticket = () => {
  const [activeTab, setActiveTab] = useState<'qr' | 'barcode'>('qr');
  const selectedTicket = useTicketStore((state) => state.selectedTicket);
  const { goToHome } = useNavigation();

  // Validar que hay ticket
  useEffect(() => {
    if (!selectedTicket) {
      goToHome();
    }
  }, [selectedTicket, goToHome]);

  // Si no hay ticket, mostrar loading mientras redirige
  if (!selectedTicket) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF6A2A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6B7280] font-medium">Redirigiendo...</p>
        </div>
      </div>
    );
  }
  
  // 🛡️ Defensive Guards
  const safeTicket = selectedTicket ?? {
    ticketNumber: 'N/A',
    price: 0,
    seatNumber: '',
    origin: 'Origen',
    destination: 'Destino',
    date: '',
    time: '',
    busLine: 'N/A',
    passengerName: 'Pasajero',
  };
  
  const bookingNumber = safeTicket.ticketNumber;
  const totalPrice = safeTicket.price;
  
  // Parse seat numbers from string (e.g., "1A, 1B" -> ["1A", "1B"])
  const seatString = safeTicket.seatNumber ?? '';
  const seats = seatString ? seatString.split(',').map(s => s.trim()) : [];

  return (
    <>
      {/* Mobile View */}
      <div className="lg:hidden min-h-screen bg-[#1E3A8A]">
        {/* Header */}
        <div className="p-4 flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <button
              onClick={goToHome}
              className="w-9 h-9 flex items-center justify-center"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="font-bold">Ticket Confirmed</h1>
              <p className="text-xs opacity-90">{selectedTicket.origin.split(' ')[0]} → {selectedTicket.destination.split(' ')[0]}</p>
            </div>
          </div>
        </div>

        {/* Ticket Card */}
        <div className="px-4 pb-6">
          <div className="bg-white rounded-[20px] overflow-hidden shadow-lg">
            {/* Trip Info */}
            <div className="p-5">
              <h2 className="text-lg font-bold text-[#111827] mb-1">
                {selectedTicket.origin.split(' ')[0]} → {selectedTicket.destination.split(' ')[0]}
              </h2>
              <p className="text-sm text-[#6B7280] mb-4">
                {selectedTicket.date} • {selectedTicket.time}
              </p>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-[#6B7280] mb-1">Compañía</p>
                  <p className="font-semibold text-[#111827]">{selectedTicket.busLine}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] mb-1">Asientos</p>
                  <div className="flex flex-wrap gap-1">
                    {seats.map((seat) => (
                      <span
                        key={seat}
                        className="px-2 py-0.5 bg-[#1E3A8A] text-white text-sm font-bold rounded-[6px]"
                      >
                        {seat}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] mb-1">Pasajero</p>
                  <p className="font-semibold text-[#111827]">{selectedTicket.passengerName}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] mb-1">Estado</p>
                  <p className="font-semibold text-[#111827]">
                    {selectedTicket.paymentStatus === 'completed' ? 'Confirmado' : 'Pendiente'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] mb-1">Salida</p>
                  <p className="font-semibold text-[#111827]">{selectedTicket.time}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] mb-1">Método de pago</p>
                  <p className="font-semibold text-[#111827]">
                    {selectedTicket.paymentMethod === 'card' ? 'Tarjeta' : 
                     selectedTicket.paymentMethod === 'spei' ? 'SPEI' : 
                     selectedTicket.paymentMethod === 'link' ? 'Link' : 'PayPal'}
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-[#E5E7EB]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#6B7280]">Total</span>
                  <span className="text-2xl font-bold text-[#FF6A2A]">₹ {totalPrice}</span>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-t border-[#E5E7EB]">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('qr')}
                  className={`flex-1 py-3 font-semibold text-sm transition-colors ${
                    activeTab === 'qr'
                      ? 'text-[#FF6A2A] border-b-2 border-[#FF6A2A]'
                      : 'text-[#6B7280]'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <QrCode className="w-4 h-4" />
                    QR
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('barcode')}
                  className={`flex-1 py-3 font-semibold text-sm transition-colors ${
                    activeTab === 'barcode'
                      ? 'text-[#FF6A2A] border-b-2 border-[#FF6A2A]'
                      : 'text-[#6B7280]'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Barcode className="w-4 h-4" />
                    Barras
                  </div>
                </button>
              </div>
            </div>

            {/* Code Display */}
            <div className="p-6 bg-[#F7F8FA]">
              {activeTab === 'qr' ? (
                <div className="text-center">
                  {/* QR Code Placeholder */}
                  <div className="w-48 h-48 mx-auto mb-4 bg-white rounded-[12px] p-4 border border-[#E5E7EB]">
                    <div className="w-full h-full bg-black relative">
                      {/* Simple QR pattern simulation */}
                      <div className="absolute inset-0 grid grid-cols-8 grid-rows-8 gap-[2px] p-2">
                        {Array.from({ length: 64 }).map((_, i) => (
                          <div
                            key={i}
                            className={`${
                              Math.random() > 0.5 ? 'bg-white' : 'bg-black'
                            }`}
                          ></div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-[#6B7280] mb-1">Booking ID: {bookingNumber}</p>
                </div>
              ) : (
                <div className="text-center">
                  {/* Barcode */}
                  <div className="w-full h-24 bg-white rounded-[12px] p-3 mb-4 border border-[#E5E7EB] flex items-center justify-center">
                    <div className="flex items-stretch h-full gap-[2px]">
                      {Array.from({ length: 40 }).map((_, i) => (
                        <div
                          key={i}
                          className="bg-black"
                          style={{
                            width: `${Math.random() > 0.3 ? 3 : 1}px`,
                          }}
                        ></div>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-[#6B7280] mb-1">Booking ID: {bookingNumber}</p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <button className="py-3 bg-white text-[#FF6A2A] font-semibold rounded-[12px] border-2 border-[#FF6A2A] hover:bg-[#FFF5F0] transition-colors">
              Cancel Ticket
            </button>
            <button className="py-3 bg-[#FF6A2A] text-white font-semibold rounded-[12px] hover:bg-[#C2410C] transition-colors flex items-center justify-center gap-2">
              <Download className="w-5 h-5" />
              Download Ticket
            </button>
          </div>
        </div>
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block min-h-screen bg-[#F7F8FA]">
        <div className="max-w-2xl mx-auto py-12 px-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-[#111827] mb-1">¡Boleto Confirmado!</h1>
              <p className="text-[#6B7280]">Tu reserva ha sido exitosa</p>
            </div>
            <button
              onClick={goToHome}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#E5E7EB] transition-colors"
            >
              <X className="w-6 h-6 text-[#6B7280]" />
            </button>
          </div>

          {/* Ticket Card */}
          <div className="bg-white rounded-[20px] overflow-hidden shadow-lg border border-[#E5E7EB]">
            {/* Trip Header */}
            <div className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white p-6">
              <h2 className="text-xl font-bold mb-2">
                {selectedTicket.origin.split(' ')[0]} → {selectedTicket.destination.split(' ')[0]}
              </h2>
              <p className="opacity-90">{selectedTicket.date} • {selectedTicket.time}</p>
            </div>

            {/* Trip Details */}
            <div className="p-6">
              <div className="grid grid-cols-3 gap-6 mb-6">
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Compañía</p>
                  <p className="font-semibold text-[#111827]">{selectedTicket.busLine}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Asientos</p>
                  <div className="flex flex-wrap gap-2">
                    {seats.map((seat) => (
                      <span
                        key={seat}
                        className="px-3 py-1 bg-[#1E3A8A] text-white font-bold rounded-[8px] text-sm"
                      >
                        {seat}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Autobús</p>
                  <p className="font-semibold text-[#111827]">TN32 IND</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6 mb-6 pb-6 border-b border-[#E5E7EB]">
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Pasajero principal</p>
                  <p className="font-semibold text-[#111827]">
                    {selectedTicket.passengerName}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Salida</p>
                  <p className="font-semibold text-[#111827]">{selectedTicket.time}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Estado de pago</p>
                  <p className="font-semibold text-[#111827]">
                    {selectedTicket.paymentStatus === 'completed' ? 'Completado' : 'Pendiente'}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between mb-6">
                <span className="text-lg font-semibold text-[#6B7280]">Total Pagado</span>
                <span className="text-3xl font-bold text-[#FF6A2A]">${totalPrice.toLocaleString('es-MX')} MXN</span>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-t border-[#E5E7EB]">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('qr')}
                  className={`flex-1 py-4 font-semibold transition-colors ${
                    activeTab === 'qr'
                      ? 'text-[#FF6A2A] border-b-2 border-[#FF6A2A] bg-[#FFF5F0]'
                      : 'text-[#6B7280] hover:bg-[#F7F8FA]'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <QrCode className="w-5 h-5" />
                    Código QR
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('barcode')}
                  className={`flex-1 py-4 font-semibold transition-colors ${
                    activeTab === 'barcode'
                      ? 'text-[#FF6A2A] border-b-2 border-[#FF6A2A] bg-[#FFF5F0]'
                      : 'text-[#6B7280] hover:bg-[#F7F8FA]'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Barcode className="w-5 h-5" />
                    Código de Barras
                  </div>
                </button>
              </div>
            </div>

            {/* Code Display */}
            <div className="p-8 bg-[#F7F8FA]">
              {activeTab === 'qr' ? (
                <div className="text-center">
                  {/* QR Code Placeholder */}
                  <div className="w-64 h-64 mx-auto mb-6 bg-white rounded-[16px] p-6 border-2 border-[#E5E7EB] shadow-sm">
                    <div className="w-full h-full bg-black relative">
                      {/* Simple QR pattern simulation */}
                      <div className="absolute inset-0 grid grid-cols-10 grid-rows-10 gap-[2px] p-2">
                        {Array.from({ length: 100 }).map((_, i) => (
                          <div
                            key={i}
                            className={`${
                              Math.random() > 0.5 ? 'bg-white' : 'bg-black'
                            }`}
                          ></div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-[#6B7280] mb-2">Escanea este código en el autobús</p>
                  <p className="text-sm font-mono text-[#111827]">Booking ID: {bookingNumber}</p>
                </div>
              ) : (
                <div className="text-center">
                  {/* Barcode */}
                  <div className="w-full max-w-md h-32 mx-auto bg-white rounded-[16px] p-4 mb-6 border-2 border-[#E5E7EB] shadow-sm flex items-center justify-center">
                    <div className="flex items-stretch h-full gap-[2px]">
                      {Array.from({ length: 60 }).map((_, i) => (
                        <div
                          key={i}
                          className="bg-black"
                          style={{
                            width: `${Math.random() > 0.3 ? 4 : 2}px`,
                          }}
                        ></div>
                      ))}
                    </div>
                  </div>
                  <p className="text-[#6B7280] mb-2">Presenta este código de barras al abordar</p>
                  <p className="text-sm font-mono text-[#111827]">Booking ID: {bookingNumber}</p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-4 mt-6">
            <button className="py-4 bg-white text-[#FF6A2A] font-semibold rounded-[14px] border-2 border-[#FF6A2A] hover:bg-[#FFF5F0] transition-colors">
              Cancelar Boleto
            </button>
            <button className="py-4 bg-[#FF6A2A] text-white font-semibold rounded-[14px] hover:bg-[#C2410C] transition-colors flex items-center justify-center gap-2">
              <Download className="w-5 h-5" />
              Descargar Boleto
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Ticket;